import { runWithCartLock } from "../concurrency/cartLock";
import { getCartStoreConfiguration } from "../config/cartRuntimeConfig";
import { readCartSnapshot, writeCartSnapshot } from "../storage/cartStorage";
import {
    CartPersistenceError,
    type CartCommitResult,
    type CartItem,
    type CartOperation,
    type CartPersistedState,
} from "../types/cart.types";
import { applyCartOperations } from "./cartOperations";

const areItemsEqual = (left: CartItem[], right: CartItem[]): boolean =>
    left === right || JSON.stringify(left) === JSON.stringify(right);

const persistOperations = (
    baseSnapshot: CartPersistedState,
    operations: CartOperation[],
): CartCommitResult => {
    const nextItems = applyCartOperations(baseSnapshot.items, operations);
    if (areItemsEqual(baseSnapshot.items, nextItems)) {
        return {
            items: baseSnapshot.items,
            revision: baseSnapshot.revision,
            changed: false,
        };
    }

    const nextSnapshot: CartPersistedState = {
        items: nextItems,
        revision: baseSnapshot.revision + 1,
        updatedAt: new Date().toISOString(),
    };
    writeCartSnapshot(nextSnapshot);

    return {
        items: nextItems,
        revision: nextSnapshot.revision,
        changed: true,
    };
};

export const commitCartOperations = async (
    operations: CartOperation[],
    legacyBaseItems: CartItem[] = [],
): Promise<CartCommitResult> => {
    if (getCartStoreConfiguration().concurrencyMode === "locked") {
        return runWithCartLock(() => persistOperations(readCartSnapshot(), operations));
    }

    const persistedSnapshot = readCartSnapshot();
    return persistOperations({ ...persistedSnapshot, items: legacyBaseItems }, operations);
};

export const replaceLegacyCartItems = async (items: CartItem[]): Promise<CartCommitResult> => {
    if (getCartStoreConfiguration().concurrencyMode === "locked") {
        throw new CartPersistenceError(
            "UNSAFE_REPLACE_DISABLED",
            "No se puede reemplazar persistentemente todo el carrito en modo locked.",
        );
    }

    const persistedSnapshot = readCartSnapshot();
    if (areItemsEqual(persistedSnapshot.items, items)) {
        return {
            items: persistedSnapshot.items,
            revision: persistedSnapshot.revision,
            changed: false,
        };
    }

    const nextSnapshot: CartPersistedState = {
        items,
        revision: persistedSnapshot.revision + 1,
        updatedAt: new Date().toISOString(),
    };
    writeCartSnapshot(nextSnapshot);

    return { items, revision: nextSnapshot.revision, changed: true };
};
